import { Video } from 'lucide-react';
import type { Task, Habit, CalendarEvent } from '@/types';
import { cn } from '@/lib/cn';
import { useEditorOpenStore } from '@/features/editor/useEditorOpen';
import { useHabitDetail } from '@/features/habits/useHabitDetail';
import { useHabitLog } from '@/features/habits/useHabitLog';
import { useTaskAction } from '@/features/lifecycle/useTaskAction';
import { hapticNav, hapticTap } from '@/features/lifecycle/haptics';
import { HabitRing } from '@/components/habits/HabitRing';
import { meetingUrlFor, openMeetingUrl } from '@/lib/meetingLink';
import { formatTimeRange } from '@/lib/time';
import { EmptyState, SWIPE_HINTS, SwipeableRow } from '@/components/ui';
import {
  formatTimeShort,
  priorityAccentClass,
  priorityLabel,
  relativeMinutesLabel,
} from './todaySelectors';

const JOIN_WINDOW_MS = 10 * 60_000;

export type UpNextItem =
  | { kind: 'task'; task: Task; start: Date }
  | { kind: 'habit'; habit: Habit; start: Date; isDone: boolean }
  | { kind: 'calendar-event'; event: CalendarEvent; start: Date };

export interface UpNextListProps {
  items: UpNextItem[];
  now: Date;
  /**
   * When false, drops the mobile horizontal padding so the list can sit inside a
   * container that already supplies its own gutters (the desktop Narrator rail).
   * Defaults to true (the mobile Today layout).
   */
  inset?: boolean;
}

export function UpNextList({ items, now, inset = true }: UpNextListProps) {
  const openEditor = useEditorOpenStore((s) => s.openEditor);
  const openHabitDetail = useHabitDetail((s) => s.open);
  const { toggleToday } = useHabitLog();
  const { complete, skip } = useTaskAction();

  if (items.length === 0) {
    return (
      <EmptyState
        tone="quiet"
        title="Nothing else scheduled."
        move="Capture below to plan ahead."
        className={cn('px-4 py-5', inset && 'mx-5')}
      />
    );
  }

  return (
    <ul className={cn('space-y-1.5', inset && 'px-5')}>
      {items.map((item) => {
        const rel = relativeMinutesLabel(item.start, now);
        if (item.kind === 'task') {
          const t = item.task;
          return (
            <li key={`task-${t.id}`}>
              <SwipeableRow
                onSwipeRight={() => complete(t.id)}
                onSwipeLeft={() => skip(t.id, 'other')}
                rightHint={SWIPE_HINTS.done}
                leftHint={SWIPE_HINTS.skip}
              >
                <button
                  type="button"
                  onClick={() => {
                    void hapticNav();
                    openEditor(t.id);
                  }}
                  className="pressable flex w-full items-center gap-3 rounded-xl border border-line bg-card px-3 py-2.5 text-left transition-colors can-hover:hover:border-line-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
                >
                  <span
                    className={cn(
                      'h-7 w-[3px] shrink-0 rounded-full',
                      priorityAccentClass(t.priority),
                    )}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body font-medium leading-tight text-t1">
                      {t.title}
                    </span>
                    <span className="mt-0.5 block text-caption text-t2">
                      {t.durationMinutes}m · {priorityLabel(t.priority)}
                    </span>
                  </span>
                  <span className="shrink-0 text-right font-mono tabular-nums">
                    <span className="block text-body font-medium text-t1">
                      {formatTimeShort(item.start)}
                    </span>
                    <span
                      className={cn(
                        'mt-0.5 block text-2xs',
                        rel.past ? 'text-t2 font-medium' : 'text-t3',
                      )}
                    >
                      {rel.label}
                    </span>
                  </span>
                </button>
              </SwipeableRow>
            </li>
          );
        }
        // habit row
        if (item.kind === 'habit') {
          const h = item.habit;
          return (
            <li key={`habit-${h.id}`}>
              <SwipeableRow
                onSwipeRight={item.isDone ? undefined : () => toggleToday(h)}
                rightHint={SWIPE_HINTS.log}
              >
                <div className="pressable flex w-full items-center gap-1 rounded-xl border border-line bg-card pe-3 transition-colors can-hover:hover:border-line-2">
                  <button
                    type="button"
                    aria-pressed={item.isDone}
                    aria-label={`${h.name} — ${item.isDone ? 'done today, tap to undo' : 'not yet done, tap to log'}`}
                    onClick={() => toggleToday(h)}
                    className="shrink-0 self-stretch flex items-center justify-center ps-3 pe-2 pointer-coarse:min-w-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
                  >
                    <HabitRing
                      state={item.isDone ? 'done' : 'todo'}
                      icon={h.icon}
                      size={22}
                      color={h.color}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void hapticNav();
                      openHabitDetail(h.id);
                    }}
                    className="min-w-0 flex-1 flex items-center justify-between gap-3 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body font-medium leading-tight text-t1">
                        {h.name}
                      </span>
                      <span className="mt-0.5 block text-caption text-t2">
                        {h.durationMinutes ? `${h.durationMinutes}m` : '15m'} · habit
                      </span>
                    </span>
                    <span className="shrink-0 text-right font-mono tabular-nums">
                      <span className="block text-body font-medium text-t1">
                        {formatTimeShort(item.start)}
                      </span>
                      <span
                        className={cn(
                          'mt-0.5 block text-2xs',
                          rel.past ? 'text-t2 font-medium' : 'text-t3',
                        )}
                      >
                        {rel.label}
                      </span>
                    </span>
                  </button>
                </div>
              </SwipeableRow>
            </li>
          );
        }
        // calendar-event row
        const e = item.event;
        const joinUrl = meetingUrlFor(e);
        const joinable =
          joinUrl !== undefined && item.start.getTime() - now.getTime() <= JOIN_WINDOW_MS;
        return (
          <li key={`cal-${e.id}`}>
            <div className="flex w-full items-center gap-3 rounded-xl border border-dashed border-line-2 bg-canvas px-3 py-2.5">
              <span className="h-7 w-[3px] shrink-0 rounded-full bg-info" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body font-medium leading-tight text-t1">
                  {e.title}
                </span>
                <span className="mt-0.5 block text-caption text-t2">
                  {formatTimeRange(e.start, e.end)} · calendar
                </span>
              </span>
              {joinable && (
                <button
                  type="button"
                  onClick={() => {
                    void hapticTap();
                    void openMeetingUrl(joinUrl);
                  }}
                  className="pressable inline-flex shrink-0 items-center gap-1 rounded-full border border-info-ink bg-info-bg px-2.5 py-1 text-caption font-medium text-t1 pointer-coarse:min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
                >
                  <Video className="h-3.5 w-3.5 text-info" aria-hidden />
                  Join
                </button>
              )}
              <span className="shrink-0 text-right font-mono tabular-nums">
                <span className="block text-body font-medium text-t1">
                  {formatTimeShort(item.start)}
                </span>
                <span
                  className={cn(
                    'mt-0.5 block text-2xs',
                    rel.past ? 'text-t2 font-medium' : 'text-t3',
                  )}
                >
                  {rel.label}
                </span>
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
